import { QueueListIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from '../../../store';
import { Modal } from '../../ui/components/Modal';
import { uiActions } from '../../ui/slice';
import { Playlist } from '../types';
import { Button } from '../../ui/components/Button';
import { ConfirmButton } from '../../ui/components/ConfirmButton';
import { TrashIcon } from '@heroicons/react/24/solid';
import { playlistActions } from '../slice';
import { MouseEvent, useMemo } from 'react';
import { formatDate } from 'date-fns';

export const LoadPlaylistModal = () => {
  const dispatch = useAppDispatch();
  const closeModal = () => dispatch(uiActions.setCurrentModal(null));

  const savedPlaylists = useAppSelector(state => state.playlist.savedPlaylists);

  const sortedPlaylists = useMemo(
    () => Object.values(savedPlaylists).sort((a, b) => (b.lastSavedAt || 0) - (a.lastSavedAt || 0)),
    [savedPlaylists]
  );

  const handleLoad = (playlistID: string) => () => {
    dispatch(playlistActions.loadPlaylist(playlistID));
    closeModal();
  };

  const handleDelete = (playlistID: string) => (evt: MouseEvent) => {
    evt.preventDefault();
    evt.stopPropagation();
    dispatch(playlistActions.deletePlaylist(playlistID));
  };

  return (
    <Modal
      height={85}
      hide={closeModal}
      modalKey='loadPlaylist'
      width={50}
    >
      <h2 className='text-2xl mb-4'>Load playlist</h2>
      <div className='grow shrink self-stretch overflow-y-auto bg-white text-neutral-700 p-2'>
        {
          sortedPlaylists.length === 0 && (
            <p className='text-neutral-400 text-center mt-8'>
              There are no saved playlists.
            </p>
          )
        }
        {
          sortedPlaylists.map(
            (playlist) => (
              <PlaylistListEntry
                key={playlist.id}
                onClick={handleLoad(playlist.id)}
                onDelete={handleDelete(playlist.id)}
                playlist={playlist}
              />
            )
          )
        }
      </div>
      <Button
        className='mt-4'
        grow={false}
        onClick={closeModal}
      >
        Close
      </Button>
    </Modal>
  );
};

type PlaylistListEntryProps = {
  onClick: () => void,
  onDelete: (e: MouseEvent) => void,
  playlist: Playlist
}

const PlaylistListEntry = ({ onClick, onDelete, playlist }: PlaylistListEntryProps) => {
  return (
    <div
      className='border m-2 p-4 rounded-sm select-none hover:bg-blue-100 cursor-pointer flex items-center'
      onClick={onClick}
    >
      <QueueListIcon className='size-6 mr-4' />
      <span className='grow'>
        { playlist.name }
      </span>
      {
        playlist.lastSavedAt && (
          <span className='text-neutral-400 mx-2'>
            { formatDate(playlist.lastSavedAt, 'd MMM yyyy h:mmaaa') }
          </span>
        )
      }
      <ConfirmButton
        cancelText='Cancel'
        caption='Are you sure you want to delete this playlist?'
        className='p-1 bg-red-600 hover:bg-red-400 text-white'
        confirmText='Delete playlist'
        grow={false}
        onConfirm={onDelete}
        title='Delete playlist'
      >
        <TrashIcon className='size-4' />
      </ConfirmButton>
    </div>
  );
};
